import { useQuery } from '@tanstack/react-query';
import { portalAPI } from '@/lib/api/portal';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn, formatCurrency } from '@/lib/utils';
import { Plus, Minus } from 'lucide-react';
import type { AddonSelection } from '@/types';

interface AddonsSelectionStepProps {
  selections: AddonSelection[];
  onChange: (selections: AddonSelection[]) => void;
  onComplete: () => void;
  onBack: () => void;
}

export function AddonsSelectionStep({
  selections,
  onChange,
  onComplete,
  onBack,
}: AddonsSelectionStepProps) {
  const { data: addonsData, isLoading } = useQuery({
    queryKey: ['addons', { isActive: true }],
    queryFn: () => portalAPI.getAddons({ isActive: true }),
  });

  const addons = addonsData?.docs || [];

  const getSelectionQuantity = (addonId: string): number => {
    const selection = selections.find(s => s.addonId === addonId);
    return selection?.quantity || 0;
  };

  const updateQuantity = (addonId: string, delta: number) => {
    const addon = addons.find(a => a.id === addonId);
    if (!addon) return;

    const currentQty = getSelectionQuantity(addonId);
    const newQty = Math.max(0, currentQty + delta);

    // Check max per order
    const maxQty = addon.maxPerOrder || Infinity;
    const clampedQty = Math.min(newQty, maxQty);

    // Check stock
    if (addon.hasInventory && addon.stockQuantity != null) {
      const available = addon.stockQuantity - addon.reservedQuantity;
      if (clampedQty > available) return;
    }

    if (clampedQty === 0) {
      onChange(selections.filter(s => s.addonId !== addonId));
    } else {
      const existingIndex = selections.findIndex(s => s.addonId === addonId);
      if (existingIndex >= 0) {
        const updated = [...selections];
        updated[existingIndex] = { ...updated[existingIndex], quantity: clampedQty };
        onChange(updated);
      } else {
        onChange([...selections, { addonId, quantity: clampedQty }]);
      }
    }
  };

  const getTotalAddonsPrice = (): number => {
    return selections.reduce((total, selection) => {
      const addon = addons.find(a => a.id === selection.addonId);
      if (!addon) return total;
      return total + (addon.price * selection.quantity);
    }, 0);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-pulse text-muted-foreground">Loading add-ons...</div>
      </div>
    );
  }

  if (addons.length === 0) {
    return (
      <div className="text-center py-12 space-y-4">
        <p className="text-muted-foreground">No add-ons available at this time.</p>
        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={onBack}>Back</Button>
          <Button onClick={onComplete}>Continue</Button>
        </div>
      </div>
    );
  }

  // Group addons by category
  const groupedAddons = addons.reduce((groups, addon) => {
    const category = addon.category || 'other';
    if (!groups[category]) groups[category] = [];
    groups[category].push(addon);
    return groups;
  }, {} as Record<string, typeof addons>);

  const categoryLabels: Record<string, string> = {
    apparel: 'Apparel',
    merchandise: 'Merchandise',
    equipment: 'Equipment',
    food: 'Food & Beverage',
    other: 'Other',
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-heading font-bold">Add-ons & Merchandise</h2>
        <p className="text-muted-foreground mt-2">
          Browse optional items to add to your registration
        </p>
      </div>

      {Object.entries(groupedAddons).map(([category, categoryAddons]) => (
        <div key={category} className="space-y-4">
          <h3 className="text-lg font-semibold">{categoryLabels[category] || category}</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {categoryAddons.map(addon => {
              const quantity = getSelectionQuantity(addon.id);
              const isOutOfStock = addon.hasInventory &&
                addon.stockQuantity != null &&
                (addon.stockQuantity - addon.reservedQuantity) <= 0;

              return (
                <Card
                  key={addon.id}
                  className={cn(
                    'transition-all',
                    quantity > 0 && 'ring-2 ring-primary border-primary',
                    isOutOfStock && 'opacity-60'
                  )}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{addon.name}</CardTitle>
                        {addon.description && (
                          <CardDescription className="text-sm mt-1">
                            {addon.description}
                          </CardDescription>
                        )}
                      </div>
                      {addon.featured && (
                        <span className="text-xs bg-secondary text-secondary-foreground px-2 py-1 rounded">
                          Featured
                        </span>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="font-semibold">{formatCurrency(addon.price)}</div>
                      {isOutOfStock ? (
                        <span className="text-sm text-destructive">Out of Stock</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateQuantity(addon.id, -1)}
                            disabled={quantity === 0}
                            aria-label={`Decrease quantity of ${addon.name}`}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="w-8 text-center font-medium" aria-live="polite">
                            {quantity}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateQuantity(addon.id, 1)}
                            aria-label={`Increase quantity of ${addon.name}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      {selections.length > 0 && (
        <div className="bg-muted rounded-lg p-4 text-center">
          <p className="text-sm text-muted-foreground">
            Selected add-ons total: <span className="font-semibold text-foreground">{formatCurrency(getTotalAddonsPrice())}</span>
          </p>
        </div>
      )}

      <div className="flex justify-center gap-4">
        <Button variant="outline" onClick={onBack}>Back</Button>
        <Button onClick={onComplete}>
          {selections.length > 0 ? 'Continue to Checkout' : 'Skip & Continue'}
        </Button>
      </div>
    </div>
  );
}
